import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../../auth/authSlice";
import DemoContentButton from "../../components/DemoContentButton";

const MobileNavbar = ({ userData, activeTab, onTabClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const menuItems = [
    { id: "back", label: "Home", icon: "fa-home", isNavigate: true, path: "/Dashboard" },
    { id: "calendar", label: "Calendar", icon: "fa-calendar" },
    { id: "sessions", label: "Sessions", icon: "fa-calendar-check-o" },
    { id: "celebrations", label: "Celebrations", icon: "fa-birthday-cake" },
    { id: "awareness", label: "Awareness", icon: "fa-lightbulb-o" },
    { id: "holidays", label: "Holidays", icon: "fa-plane" },
    { id: "competitions", label: "Competitions", icon: "fa-trophy" },
    { id: "exams", label: "Exams", icon: "fa-pencil" },
    { id: "ptms", label: "PTM", icon: "fa-users" },
    { id: "workingDays", label: "Working Days", icon: "fa-calculator" },
    { id: "printCalendar", label: "Print", icon: "fa-print" },
  ];

  // Toggle menu
  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  // Handle menu item click
  const handleMenuClick = (item) => {
    setIsOpen(false); // Close menu
    
    if (item.isNavigate && item.path) {
      navigate(item.path);
    } else {
      onTabClick(item.id);
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsOpen(false);
    dispatch(logout());
    navigate("/home");
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("touchstart", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile Top Bar - Only visible on mobile */}
      <div className="mobile-navbar-wrapper d-lg-none">
        <div className="mobile-top-bar">
          {/* Hamburger Button */}
          <button
            ref={toggleRef}
            className="mobile-hamburger-btn"
            onClick={handleToggle}
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            <div className={`hamburger-lines ${isOpen ? "open" : ""}`}>
              <span className="line line1"></span>
              <span className="line line2"></span>
              <span className="line line3"></span>
            </div>
          </button>

          <div className="d-flex align-items-center gap-2">
            <DemoContentButton moduleName="Academic Calendar" />

            {/* Profile Dropdown */}
            <div className="dropdown">
              <button
                className="btn btn-light btn-sm dropdown-toggle d-flex align-items-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa fa-user"></i>
                <span className="ms-2">{userData?.name}</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <span className="dropdown-item" style={{ cursor: "pointer" }}>
                    Profile
                  </span>
                </li>
                <li>
                  <Link to="/ai-integrate" className="dropdown-item">
                    Use AI
                  </Link>
                </li>
                <li>
                  <span
                    className="dropdown-item"
                    onClick={handleLogout}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fa fa-sign-out me-2"></i>
                    Log Out
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Overlay */}
        <div
          className={`mobile-menu-overlay ${isOpen ? "show" : ""}`}
          onClick={() => setIsOpen(false)}
        ></div>

        {/* Sliding Menu */}
        <div ref={menuRef} className={`mobile-slide-menu ${isOpen ? "open" : ""}`}>
          {/* Menu Header */}
          <div className="mobile-menu-header">
            <span>Menu</span>
            <button className="close-menu-btn" onClick={() => setIsOpen(false)}>
              <i className="fa fa-times"></i>
            </button>
          </div>

          {/* Menu Items Grid */}
          <div className="mobile-menu-grid">
            {menuItems.map((item) => (
              <button
                key={item.id}
                className={`mobile-menu-item ${activeTab === item.id ? "active" : ""}`}
                onClick={() => handleMenuClick(item)}
              >
                <i className={`fa ${item.icon}`}></i>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        /* Mobile Navbar Wrapper */
        .mobile-navbar-wrapper {
          position: relative;
          width: 100%;
        }

        /* Top Bar */
        .mobile-top-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
        }

        /* Hamburger Button */
        .mobile-hamburger-btn {
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.5);
          border-radius: 4px;
          padding: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hamburger-lines {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          width: 22px;
          height: 16px;
        }

        .hamburger-lines .line {
          display: block;
          height: 2px;
          width: 100%;
          background-color: white;
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        .hamburger-lines.open .line1 {
          transform: rotate(45deg) translate(5px, 5px);
        }

        .hamburger-lines.open .line2 {
          opacity: 0;
        }

        .hamburger-lines.open .line3 {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        /* Overlay */
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1040;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }

        .mobile-menu-overlay.show {
          opacity: 1;
          visibility: visible;
        }

        /* Sliding Menu */
        .mobile-slide-menu {
          position: fixed;
          top: 0;
          left: -280px;
          width: 280px;
          height: 100vh;
          background: linear-gradient(135deg, #7B2FF7 0%, #9B30FF 50%, #F40076 100%);
          z-index: 1050;
          transition: left 0.3s ease;
          overflow-y: auto;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.3);
        }

        .mobile-slide-menu.open {
          left: 0;
        }

        /* Menu Header */
        .mobile-menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          font-size: 18px;
          font-weight: 600;
        }

        .close-menu-btn {
          background: transparent;
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          padding: 4px 8px;
        }

        .close-menu-btn:hover {
          opacity: 0.8;
        }

        /* Menu Grid */
        .mobile-menu-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          padding: 20px 16px;
        }

        /* Menu Item */
        .mobile-menu-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 16px 8px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          min-height: 80px;
          gap: 8px;
        }

        .mobile-menu-item:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: scale(1.02);
        }

        .mobile-menu-item.active {
          background: white;
          color: #7B2FF7;
          border-color: white;
        }

        .mobile-menu-item i {
          font-size: 24px;
        }

        .mobile-menu-item span {
          font-size: 11px;
          font-weight: 500;
          text-align: center;
          line-height: 1.2;
        }

        /* Dropdown styling */
        .mobile-top-bar .dropdown-item {
          cursor: pointer;
        }

        .mobile-top-bar .dropdown-item:hover {
          background-color: #f0f0f0;
        }
      `}</style>
    </>
  );
};

export default MobileNavbar;